import { useObservableState } from 'observable-hooks';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { map } from 'rxjs/operators';
import * as yup from 'yup';
import Group from '../../../../../components/form/elements/group';
import Select, {
  emptySelectOption,
  Option,
} from '../../../../../components/form/elements/select';
import Text from '../../../../../components/form/elements/text';
import Address from '../../../../../components/form/elements/address';
import TextArea from '../../../../../components/form/elements/text-area';
import { useService } from '../../../../../services/services';
import css from './create-warehouse-modal-form.module.css';

export function useWarehouseForm() {
  const { t } = useTranslation();
  const { farmId } = useParams<{
    farmId: string;
  }>();
  const [warehouseBody, setWarehouseBody] = useState([]);
  const { warehouseService } = useService();

  const [typesOfWarehouses] = useObservableState<Option[]>(
    () =>
      warehouseService.typesOfWarehouses(farmId).pipe(
        map((values) =>
          values.map((value) => {
            return { value: value.id, label: value.value };
          })
        )
      ),
    null
  );

  useEffect(() => {
    if (typesOfWarehouses !== null) {
      setWarehouseBody([
        Group([
          Text(
            'name',
            t('pages.warehouse.warehouses.name'),
            undefined,
            undefined,
            undefined,
            undefined,
            true
          ),
          Select(
            'type_of_warehouse_id',
            {
              options: typesOfWarehouses,
              multi: false,
              label: 'pages.warehouse.warehouses.type',
            },
            true
          ),
          Address(
            'address',
            t('pages.warehouse.warehouses.address'),
            true,
            true
          ),
          Text(
            'coordinates',
            t('pages.warehouse.warehouses.coordinates'),
            undefined,
            undefined,
            undefined,
            undefined,
            true,
            <p className={css['coordinate_example']}>
              {t('pages.warehouse.products.coordinate_example')}
            </p>
          ),
          Text(
            'department_pvd_nr',
            t('pages.warehouse.products.department_pvd_nr'),
            undefined,
            undefined,
            undefined,
            undefined,
            true
          ),
          TextArea(
            'comment',
            t('pages.warehouse.warehouses.comment'),
            undefined,
            undefined,
            undefined,
            true
          ),
        ]),
      ]);
    }
  }, [typesOfWarehouses, t, farmId]);

  return warehouseBody;
}

export const warehouseSchema = (
  additionalFields = {
    department_pvd_nr: false,
  }
) => {
  return yup.object().shape({
    name: yup.string().max(255).required(),
    department_pvd_nr: additionalFields.department_pvd_nr
      ? yup.string().max(255)
      : null,
    type_of_warehouse_id: yup.string().required(),
    address: yup.lazy((addressValue) => {
      if (typeof addressValue === 'object') {
        return yup.object().shape({
          value: yup.string().required(),
        });
      } else {
        return yup.string().max(255).required();
      }
    }),
    coordinates: yup
      .mixed()
      .test('coordinates', 'Field must contain valid coordinates', (value) => {
        if (!value) {
          return true;
        }
        const latLong = value.split(',');
        if (latLong.length !== 2) {
          return false;
        }
        const lat = Number(latLong[0]);
        const long = Number(latLong[1]);
        if (Number.isNaN(lat) || Number.isNaN(long)) {
          return false;
        }
        return Math.abs(lat) <= 90 && Math.abs(long) <= 180;
      }),
  });
};

export const defaultValues = {
  type_of_warehouse_id: emptySelectOption,
  address: '',
  coordinates: '',
  active: true,
  comment: '',
  department_pvd_nr: '',
  warehouseId: '',
};
